import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AdminShell } from "@/components/admin/AdminShell";
import { AdminCommandPalette } from "@/components/admin/AdminCommandPalette";
import { AdminOverview } from "@/pages/AdminOverview";
import { AdminAnalytics } from "@/pages/AdminAnalytics";
import { AdminVisitorSessions } from "@/pages/AdminVisitorSessions";
import { AdminAuditLog } from "@/pages/AdminAuditLog";
import { AdminAccessLog } from "@/pages/AdminAccessLog";
import { AdminUsersManager } from "@/pages/AdminUsersManager";
import { AdminWaitlistManager } from "@/pages/AdminWaitlistManager";
import { AdminWaitlistFormBuilder } from "@/pages/AdminWaitlistFormBuilder";
import { AdminContactManager } from "@/pages/AdminContactManager";
import { AdminTicketsManager } from "@/pages/AdminTicketsManager";
import { AdminCompaniesManager } from "@/pages/AdminCompaniesManager";
import { AdminDataRoomManager } from "@/pages/AdminDataRoomManager";
import { AdminProgramsManager } from "@/pages/AdminProgramsManager";
import { AdminJourneyTypesManager } from "@/pages/AdminJourneyTypesManager";
import { AdminLandingSectionsManager } from "@/pages/AdminLandingSectionsManager";
import { AdminMarqueeManager } from "@/pages/AdminMarqueeManager";
import { AdminPagesManager } from "@/pages/AdminPagesManager";
import { AdminSiteSettings } from "@/pages/AdminSiteSettings";
import { AdminSeoPanel } from "@/pages/AdminSeoPanel";
import { AdminMemberActivity } from "@/pages/AdminMemberActivity";
import { AdminOnboardingPlan } from "@/pages/AdminOnboardingPlan";
import { AdminCompanyTypeRequests } from "@/pages/AdminCompanyTypeRequests";
import { AdminCountryInsights } from "@/pages/AdminCountryInsights";
import {
  Activity,
  BarChart3,
  Users,
  Briefcase,
  LayoutGrid,
  Settings,
} from "lucide-react";

/** Legacy `?section=` values kept working after the nav consolidation. */
const ALIASES = {
  analytics: "visitors",
  ecosystem: "overview",
  access_log: "access_log",
};

export default function AdminPanel() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [commandOpen, setCommandOpen] = useState(false);

  const rawSection = searchParams.get("section") || "overview";
  const activeLeaf = ALIASES[rawSection] || rawSection;

  const sections = useMemo(
    () => [
      {
        id: "overview",
        label: t("adminConsole.groupOverview", { defaultValue: "Overview" }),
        shortLabel: t("adminConsole.groupOverviewShort", { defaultValue: "Home" }),
        icon: BarChart3,
        items: [{ id: "overview", label: t("adminOverview.pageTitle", { defaultValue: "Platform snapshot" }) }],
      },
      {
        id: "insights",
        label: t("adminConsole.groupInsights", { defaultValue: "Visibility" }),
        shortLabel: t("adminConsole.groupInsightsShort", { defaultValue: "Insight" }),
        icon: Activity,
        items: [
          { id: "visitors", label: t("adminConsole.tabVisitors", { defaultValue: "Traffic" }) },
          { id: "sessions", label: t("adminVisitors.title", { defaultValue: "Visitor journeys" }) },
          { id: "activity", label: t("adminActivity.title", { defaultValue: "Change log" }) },
          { id: "access_log", label: t("adminConsole.tabSignIns", { defaultValue: "Sign-ins" }) },
          { id: "countries", label: t("countryInsights.title", { defaultValue: "Countries" }) },
        ],
      },
      {
        id: "people",
        label: t("adminConsole.groupPeople", { defaultValue: "People" }),
        shortLabel: t("adminConsole.groupPeopleShort", { defaultValue: "People" }),
        icon: Users,
        items: [
          { id: "users", label: t("usersManager.title", { defaultValue: "Users & roles" }) },
          { id: "member_activity", label: t("memberActivity.title", { defaultValue: "Member activity" }) },
          { id: "waitlist", label: t("admin.waitlist", { defaultValue: "Waitlist" }) },
          { id: "waitlist_form", label: t("admin.waitlistForm", { defaultValue: "Waitlist form" }) },
          { id: "contact", label: t("admin.contactSubmissions", { defaultValue: "Inbox" }) },
          { id: "tickets", label: t("tickets.adminNav", { defaultValue: "Support tickets" }) },
        ],
      },
      {
        id: "deals",
        label: t("adminConsole.groupDeals", { defaultValue: "Portfolio" }),
        shortLabel: t("adminConsole.groupDealsShort", { defaultValue: "Deals" }),
        icon: Briefcase,
        items: [
          { id: "companies", label: t("adminCompanies.navTitle", { defaultValue: "Companies" }) },
          { id: "data_room", label: t("dataRoom.navTitle", { defaultValue: "Data rooms" }) },
          { id: "programs", label: t("adminConsole.tabPrograms", { defaultValue: "Programs" }) },
          { id: "journey_types", label: t("adminConsole.tabJourneyTypes", { defaultValue: "Journey types" }) },
          { id: "type_requests", label: t("companyType.adminTitle", { defaultValue: "Business type requests" }) },
        ],
      },
      {
        id: "content",
        label: t("adminConsole.groupContent", { defaultValue: "Site content" }),
        shortLabel: t("adminConsole.groupContentShort", { defaultValue: "Content" }),
        icon: LayoutGrid,
        items: [
          { id: "landing_sections", label: t("adminLanding.navTitle", { defaultValue: "Homepage" }) },
          { id: "marquee", label: t("marquee.adminSection", { defaultValue: "Marquee" }) },
          { id: "custom_pages", label: t("admin.customPages", { defaultValue: "Pages" }) },
          { id: "footer", label: t("admin.footer", { defaultValue: "Footer" }) },
          { id: "branding", label: t("admin.branding", { defaultValue: "Branding" }) },
        ],
      },
      {
        id: "settings",
        label: t("admin.settings", { defaultValue: "Settings" }),
        shortLabel: t("adminConsole.groupSettingsShort", { defaultValue: "Setup" }),
        icon: Settings,
        items: [
          { id: "settings", label: t("admin.settings", { defaultValue: "Platform settings" }) },
          { id: "seo", label: t("seo.title", { defaultValue: "SEO & search visibility" }) },
          { id: "onboarding_plan", label: t("onboardingPlan.title", { defaultValue: "Onboarding plan" }) },
        ],
      },
    ],
    [t],
  );

  const activeGroup = useMemo(() => {
    const hit = sections.find((s) => s.items.some((it) => it.id === activeLeaf));
    return (hit || sections[0]).id;
  }, [sections, activeLeaf]);

  const selectLeaf = useCallback((leafId) => {
    const next = new URLSearchParams(searchParams);
    // Section switches invalidate row-level deep-link params.
    ["role", "stage", "overdue", "enrolled", "graduated", "q", "tab"].forEach((k) => next.delete(k));
    if (leafId && leafId !== "overview") next.set("section", leafId);
    else next.delete("section");
    setSearchParams(next, { replace: false });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [searchParams, setSearchParams]);

  // ⌘K / Ctrl-K anywhere in the console; `/` when nothing is focused.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setCommandOpen((v) => !v);
        return;
      }
      if (e.key === "/" && !commandOpen) {
        const tag = (e.target?.tagName || "").toLowerCase();
        if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
        e.preventDefault();
        setCommandOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [commandOpen]);

  const dataRoomOpenId = searchParams.get("room") || null;
  const companyOpenId = searchParams.get("company") || null;
  const openDataRoom = useCallback((roomId) => {
    const next = new URLSearchParams(searchParams);
    next.set("section", "data_room");
    if (roomId) next.set("room", roomId);
    setSearchParams(next);
  }, [searchParams, setSearchParams]);
  const openCompany = useCallback((companyId) => {
    const next = new URLSearchParams(searchParams);
    next.set("section", "companies");
    if (companyId) next.set("company", companyId);
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  const PANELS = {
    overview: <AdminOverview />,
    visitors: <AdminAnalytics />,
    sessions: <AdminVisitorSessions />,
    activity: <AdminAuditLog />,
    access_log: <AdminAccessLog />,
    users: <AdminUsersManager />,
    member_activity: <AdminMemberActivity />,
    waitlist: <AdminWaitlistManager />,
    waitlist_form: <AdminWaitlistFormBuilder />,
    contact: <AdminContactManager />,
    tickets: <AdminTicketsManager />,
    companies: <AdminCompaniesManager initialCompanyId={companyOpenId} onOpenDataRoom={openDataRoom} />,
    data_room: <AdminDataRoomManager initialRoomId={dataRoomOpenId} onNavigateToCompany={openCompany} />,
    programs: <AdminProgramsManager />,
    journey_types: <AdminJourneyTypesManager />,
    type_requests: <AdminCompanyTypeRequests />,
    countries: <AdminCountryInsights />,
    landing_sections: <AdminLandingSectionsManager />,
    marquee: <AdminMarqueeManager />,
    custom_pages: <AdminPagesManager />,
    footer: <AdminSiteSettings panel="footer" />,
    branding: <AdminSiteSettings panel="branding" />,
    settings: <AdminSiteSettings panel="settings" />,
    seo: <AdminSeoPanel />,
    onboarding_plan: <AdminOnboardingPlan />,
  };

  return (
    <AdminShell
      sections={sections}
      activeGroup={activeGroup}
      activeLeaf={activeLeaf}
      onSelectLeaf={selectLeaf}
      onOpenCommand={() => setCommandOpen(true)}
    >
      <AdminCommandPalette open={commandOpen} onOpenChange={setCommandOpen} />
      <div data-testid="admin-panel-page">{PANELS[activeLeaf] || PANELS.overview}</div>
    </AdminShell>
  );
}
